import React, { useEffect, useState } from "react";
import {
  Users, Layers, IndianRupee, CreditCard,
  MessageSquare, Trophy, ArrowRight,
  GraduationCap, Clock
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import StatCard from "./StatCard";
import RecentPayments from "./RecentPayments";
import UpcomingBatches from "./UpcomingBatches";
import UpgradeBanner from "./UpgradeBanner";
import GracePeriodBanner from "./GracePeriodBanner";
import RevenueShareBanner from "./RevenueShareBanner";
import LatestPlacements from "./LatestPlacements";
import TodaySchedule from "./UpcomingInterviews";
import DashboardAvatar from "./DashboardAvatar";
import apiClient from "../../utils/apiClient";
import { useNavigate } from "react-router-dom";
import { convertToIndianCurrency, timeAgoIST } from "../../utils/common";
import { useBranch } from "../../contexts/BranchContext";
import PageHeader from "../ui/PageHeader";
import "./dashboard.scss";

// ── Helpers ──
function formatCompact(amount) {
  if (!amount || isNaN(amount)) return "₹0";
  if (amount >= 100000) return `${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
  return `₹${amount}`;
}

const CHART_COLORS = {
  collected: "#2563eb",
  pending: "#d97706",
  bar: ["#2563eb", "#059669", "#7c3aed", "#d97706", "#e11d48", "#0891b2", "#ea580c", "#4f46e5"],
  pie: ["#2563eb", "#d97706"],
};

const tooltipStyle = {
  backgroundColor: "var(--card-bg, #fff)",
  border: "1px solid var(--border-primary, #e5e7eb)",
  borderRadius: "8px",
  fontSize: "12px",
  padding: "8px 12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  color: "var(--text-primary, #111827)",
};

// ── Revenue Chart ──
function RevenueChart({ data = [] }) {
  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-icon emerald"><IndianRupee size={18} /></div>
          <div>
            <div className="db-card-title">Revenue Overview</div>
            <div className="db-card-subtitle">Monthly fee collection trend</div>
          </div>
        </div>
        <div className="db-chart-legend">
          <span className="db-chart-legend-item">
            <span className="db-legend-dot" style={{ background: CHART_COLORS.collected }} />Collected
          </span>
          <span className="db-chart-legend-item">
            <span className="db-legend-dot" style={{ background: CHART_COLORS.pending }} />Pending
          </span>
        </div>
      </div>
      <div className="db-card-body">
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gradCollected" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.collected} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={CHART_COLORS.collected} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradPending" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.pending} stopOpacity={0.1} />
                  <stop offset="95%" stopColor={CHART_COLORS.pending} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary, #e5e7eb)" vertical={false} />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "var(--text-tertiary, #94a3b8)" }} dy={8} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "var(--text-tertiary, #94a3b8)" }} tickFormatter={(v) => v >= 1000 ? `${v / 1000}K` : v} dx={-4} />
              <Tooltip contentStyle={tooltipStyle} formatter={(value) => [convertToIndianCurrency(value)]} />
              <Area type="monotone" dataKey="collected" stroke={CHART_COLORS.collected} strokeWidth={2.5} fill="url(#gradCollected)" dot={false} activeDot={{ r: 5, strokeWidth: 2, fill: "#fff" }} />
              {data[0]?.pending !== undefined && (
                <Area type="monotone" dataKey="pending" stroke={CHART_COLORS.pending} strokeWidth={2} strokeDasharray="5 5" fill="url(#gradPending)" dot={false} />
              )}
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="db-empty">No revenue data available</div>
        )}
      </div>
    </div>
  );
}

// ── Fee Collection Donut ──
function FeeCollectionChart({ collected, pending }) {
  const total = collected + pending;
  const percent = total > 0 ? Math.round((collected / total) * 100) : 0;
  const pieData = [
    { name: "Collected", value: collected || 0.01 },
    { name: "Pending", value: pending || 0.01 },
  ];
  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-icon blue"><CreditCard size={18} /></div>
          <div>
            <div className="db-card-title">Fee Collection</div>
            <div className="db-card-subtitle">Overall collection status</div>
          </div>
        </div>
      </div>
      <div className="db-card-body">
        <div className="db-donut-wrapper">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={58}
                outerRadius={78}
                paddingAngle={3}
                dataKey="value"
                strokeWidth={0}
                startAngle={90}
                endAngle={-270}
              >
                {pieData.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS.pie[i]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(value) => [convertToIndianCurrency(value)]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="db-donut-center">
            <div className="db-donut-percent">{percent}%</div>
            <div className="db-donut-label">Collected</div>
          </div>
        </div>
        <div className="db-donut-breakdown">
          <div className="db-donut-row">
            <div className="db-donut-row-left">
              <span className="db-legend-dot" style={{ background: CHART_COLORS.pie[0] }} />Collected
            </div>
            <span className="db-donut-row-val">{formatCompact(collected)}</span>
          </div>
          <div className="db-donut-row">
            <div className="db-donut-row-left">
              <span className="db-legend-dot" style={{ background: CHART_COLORS.pie[1] }} />Pending
            </div>
            <span className="db-donut-row-val">{formatCompact(pending)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Course Enrollment Bar Chart ──
function CourseEnrollmentChart({ data = [] }) {
  const total = data.reduce((s, c) => s + c.students, 0);
  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-icon violet"><Layers size={18} /></div>
          <div>
            <div className="db-card-title">Course-wise Enrollment</div>
            <div className="db-card-subtitle">Student distribution across courses</div>
          </div>
        </div>
        <span className="db-badge">{total} Total</span>
      </div>
      <div className="db-card-body">
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }} barSize={40}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary, #e5e7eb)" vertical={false} />
              <XAxis dataKey="course" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "var(--text-tertiary, #94a3b8)" }} dy={8} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "var(--text-tertiary, #94a3b8)" }} dx={-4} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
              <Bar dataKey="students" radius={[6, 6, 0, 0]}>
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.color || CHART_COLORS.bar[i % CHART_COLORS.bar.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="db-empty">No course data available</div>
        )}
      </div>
    </div>
  );
}

// ── Enquiry Funnel ──
function EnquiryFunnelCard({ data = [] }) {
  const maxCount = data.length > 0 ? Math.max(...data.map(e => e.count), 1) : 1;
  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-icon amber"><MessageSquare size={18} /></div>
          <div>
            <div className="db-card-title">Enquiry Pipeline</div>
            <div className="db-card-subtitle">This month's funnel</div>
          </div>
        </div>
      </div>
      <div className="db-card-body">
        {data.length > 0 ? data.map((stage) => (
          <div key={stage.stage} className="db-funnel-item">
            <div className="db-funnel-label">
              <span className="db-funnel-stage">{stage.stage}</span>
              <span className="db-funnel-count">{stage.count}</span>
            </div>
            <div className="db-funnel-bar-track">
              <div
                className="db-funnel-bar-fill"
                style={{
                  width: `${(stage.count / maxCount) * 100}%`,
                  backgroundColor: stage.color
                }}
              />
            </div>
          </div>
        )) : (
          <div className="db-empty">No enquiries yet</div>
        )}
      </div>
    </div>
  );
}

// ── Recent Activity (mixed feed) ──
function RecentActivityCard({ students = [], payments = [], navigate }) {
  // Build a unified activity feed from students and payments
  const activities = [];

  students.forEach(s => {
    activities.push({
      id: `s-${s._id}`,
      type: "enrollment",
      icon: <GraduationCap size={14} />,
      msg: <><strong>{s.name}</strong> enrolled in <strong>{s.course?.name || "a course"}</strong></>,
      time: s.createdAt,
    });
  });

  payments.forEach(p => {
    activities.push({
      id: `p-${p._id}`,
      type: "payment",
      icon: <IndianRupee size={14} />,
      msg: <><strong>{p.studentId?.name || "Student"}</strong> paid <strong>{convertToIndianCurrency(p.currentPayment)}</strong> for <strong>{p.studentId?.course?.name || "a course"}</strong></>,
      time: p.date,
    });
  });

  // Sort by time descending, take top 5
  activities.sort((a, b) => new Date(b.time) - new Date(a.time));
  const topActivities = activities.slice(0, 5);

  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-icon primary"><Clock size={18} /></div>
          <div>
            <div className="db-card-title">Recent Activity</div>
            <div className="db-card-subtitle">Latest updates across the institute</div>
          </div>
        </div>
        <button className="db-card-link" onClick={() => navigate("/students")}>
          View All <ArrowRight size={12} />
        </button>
      </div>
      <div className="db-card-body">
        {topActivities.length > 0 ? topActivities.map((a) => (
          <div key={a.id} className="db-activity-item">
            <div className={`db-activity-icon ${a.type}`}>{a.icon}</div>
            <div className="db-activity-content">
              <div className="db-activity-msg">{a.msg}</div>
              <div className="db-activity-time">{timeAgoIST(a.time)}</div>
            </div>
          </div>
        )) : (
          <div className="db-empty">No recent activity</div>
        )}
      </div>
    </div>
  );
}

// ── Top Performers ──
function TopPerformersCard({ students = [], navigate }) {
  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-icon amber"><Trophy size={18} /></div>
          <div>
            <div className="db-card-title">Top Performers</div>
            <div className="db-card-subtitle">Based on overall score</div>
          </div>
        </div>
        <button className="db-card-link" onClick={() => navigate("/students")}>
          View All <ArrowRight size={12} />
        </button>
      </div>
      <div className="db-card-body">
        {students.length > 0 ? students.slice(0, 5).map((s, i) => (
          <div key={s._id || i} className="db-list-item">
            <div className="db-list-rank">{i + 1}</div>
            <DashboardAvatar name={s.name} />
            <div className="db-list-info">
              <div className="db-list-name">{s.name}</div>
              <div className="db-list-sub">{s.course?.name || ""}</div>
            </div>
            <div className="db-list-right">
              <div className="db-list-value">{s.score ? `${s.score}%` : "—"}</div>
              {s.score && <div className="db-list-value-sub">Score</div>}
            </div>
          </div>
        )) : (
          <div className="db-empty">No data available</div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// Main Dashboard
// ═══════════════════════════════════════
const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const { selectedBranchId } = useBranch();

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get("/dashboard");
        setDashboard(data);
      } catch {
        setDashboard(null);
      }
      setLoading(false);
    };
    fetchDashboard();
  }, [selectedBranchId]);

  if (loading)
    return (
      <div className="db-page">
        <div className="db-loading">
          <div className="db-loading-spinner" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );

  if (!dashboard)
    return (
      <div className="db-page">
        <div className="db-empty-state">
          <p>Failed to load dashboard data. Please try again.</p>
        </div>
      </div>
    );

  const {
    stats = {}, courseEnrollment = [], revenueData = [], feeCollection = {},
    enquiryFunnel = [], todaySchedule = [], upcomingBatches = [],
    latestPlacements = [], recentStudents = [], recentPayments = []
  } = dashboard;

  return (
    <div className="db-page">

      {/* ── Page Title ── */}
      <PageHeader
        icon={Users}
        title="Dashboard"
        description="Overview of your institute's performance and activity"
      />

      {/* ── Grace Period / Expiry Banner ── */}
      <GracePeriodBanner />

      {/* ── Revenue-Share Unpaid Invoice Banner ── */}
      <RevenueShareBanner />

      {/* ── Upgrade Upsell Banner ── */}
      <UpgradeBanner />

      {/* ── KPI Cards ── */}
      <div className="db-grid-4">
        <StatCard
          title="TOTAL STUDENTS"
          value={stats.totalStudents || 0}
          change={stats.studentsGrowth || null}
          changeType="positive"
          icon={<Users size={20} />}
          iconClass="blue"
          onClick={() => navigate("/students")}
        />
        <StatCard
          title="ACTIVE BATCHES"
          value={stats.activeBatches || 0}
          change={null}
          changeType="positive"
          icon={<Layers size={20} />}
          iconClass="violet"
          onClick={() => navigate("/batches")}
        />
        <StatCard
          title="TOTAL REVENUE"
          value={formatCompact(stats.totalCollectedFees || 0)}
          change={null}
          changeType="positive"
          icon={<IndianRupee size={20} />}
          iconClass="emerald"
          onClick={() => navigate("/fees")}
        />
        <StatCard
          title="PENDING FEES"
          value={formatCompact(stats.totalPendingFees || 0)}
          change={`From ${stats.newEnquiries || 0} students`}
          changeType="negative"
          icon={<CreditCard size={20} />}
          iconClass="rose"
          onClick={() => navigate("/fees")}
        />
      </div>

      {/* ── Revenue + Fee Collection ── */}
      <div className="db-grid-3-1">
        <RevenueChart data={revenueData} />
        <FeeCollectionChart
          collected={feeCollection?.collected || 0}
          pending={feeCollection?.pending || 0}
        />
      </div>

      {/* ── Course Enrollment + Enquiry Funnel ── */}
      <div className="db-grid-3-1">
        <CourseEnrollmentChart data={courseEnrollment} />
        <EnquiryFunnelCard data={enquiryFunnel} />
      </div>

      {/* ── Today's Schedule + Recent Activity ── */}
      <div className="db-grid-2">
        <TodaySchedule schedule={todaySchedule} />
        <RecentActivityCard students={recentStudents} payments={recentPayments} navigate={navigate} />
      </div>

      {/* ── Top Performers + Upcoming Batches + Latest Placements ── */}
      <div className="db-grid-3">
        <TopPerformersCard students={recentStudents} navigate={navigate} />
        <UpcomingBatches batches={upcomingBatches} />
        <LatestPlacements placements={latestPlacements} />
      </div>

      {/* ── Recent Payments ── */}
      <RecentPayments payments={recentPayments} />
    </div>
  );
};

export default Dashboard;
